/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { Column, Row } from '../../../layout/flexbox';
import * as Buttons from '../../../forms/elements/buttons';
import { Input } from '../../../forms/elements/Input';
import { COLUMN_SPACING, ROW_SPACING } from '../../../forms/styles';
import LabelGroup from '../../../forms/elements/LabelGroup';
import CopyToClipboardButton from '../../../copyToClipboard/CopyToClipboardButton';
import { assoc } from '../../../language/module';
import { getResource } from '../../../language/resources/getResource';
import { MAX_REDEMPTION_REFERENCE_LENGTH } from './module';


/**
 * RedemptionIdForm
 * This component shows the redemption ID and allows for it to be copied to clipboard
 * It also has an input for local refence and a save button to submit it.
 *
 * @param redemptionId - The Redemption ID returned when a redemption succeeds
 * @param onLocalRefSave - Action Handler for when the user clicks to save the local reference
 * @param children - Shown above the save button
 */
export class RedemptionIdForm extends React.PureComponent<{
  redemptionId: string | undefined,
  onLocalRefSave: (localRef: string, redemptionId: string | undefined) => void,
  rootCss?: SerializedStyles,
  resources: Record<string, any>,
  children?: React.ReactNode,
}, {
  localRef: string,
}> {
  public state = {
    localRef: '',
  };


  private onLocalRefChange = event => {
    const val = event.target.value;
    this.setState(prev => assoc('localRef', val, prev));
  };


  private onSaveAndCloseClick = () => {
    this.props.onLocalRefSave(this.state.localRef, this.props.redemptionId);
  };


  public render = () => (
    <Column rootCss={css`
                        ${COLUMN_SPACING};
                        ${this.props.rootCss};
                      `}>
    <LabelGroup label={getResource(this.props.resources, 'REDEMPTION_ID_LABEL')} rootCss={css`pointer-events: none;`}>
    <CopyToClipboardButton textToCopy={this.props.redemptionId || ''}
                          title={getResource(this.props.resources, 'REDEMPTION_ID_TITLE')}
                          buttonTitle={getResource(this.props.resources, 'REDEMPTION_ID_BUTTON_TITLE')}/>
    </LabelGroup>

    <LabelGroup label={getResource(this.props.resources, 'REDEMPTION_ID_REFERENCE_LABEL')}>
    <Input title={getResource(this.props.resources, 'REDEMPTION_ID_REFERENCE_TITLE')}
          type='text'
          maxLength={MAX_REDEMPTION_REFERENCE_LENGTH}
          autoFocus={true}
          value={this.state.localRef}
          onChange={this.onLocalRefChange}
          placeholder={getResource(this.props.resources, 'REDEMPTION_ID_REFERENCE_TITLE')}
          css={css`flex-grow: 1;`}/>
    </LabelGroup>

    {this.props.children}

    <Row justifyContent='flex-end' rootCss={css`padding-top: 25px; padding-bottom: 1px; ${ROW_SPACING};`}>
      <Buttons.BigNeutral title={getResource(this.props.resources, 'REDEMPTION_ID_SAVE_BUTTON_TITLE')}
                        onClick={this.onSaveAndCloseClick}
                        css={css`align-self: flex-end;`}>
        {getResource(this.props.resources, 'REDEMPTION_ID_SAVE_BUTTON')}
      </Buttons.BigNeutral>
    </Row>
    </Column>
  );
}
